import React from 'react'
import { PersistGate } from "redux-persist/integration/react"
import { Provider } from "react-redux"
import { BrowserRouter } from "react-router-dom"
import store, { persistor } from "@/store/store"
import SplashScreen from '@/components/splash-screen'
import AuthBootstrapGate from '@/components/auth/AuthBootstrapGate'
import RestaurantBootstrapGate from '@/components/restaurant/RestaurantBootstrapGate'
import PublicTenantGate from '@/components/restaurant/PublicTenantGate'
import CartTenantHydrationGate from '@/components/cart/CartTenantHydrationGate'
import { PublicTenantProvider, usePublicTenant } from '@/context/PublicTenantProvider'
import { RestaurantConfigurationProvider } from '@/context/RestaurantConfigurationProvider'
import AppThemeProvider from '@/theme/AppThemeProvider'
import WebRoutes from '@/web-routes/web-routes'
import PlatformRoutes from '@/web-routes/platform-routes'
import PaymentGatewayResponse from '@/pages/payment-gateway-response'
import { logMenuDiag } from '@/utils/menu-load-diagnostics'


/**
 * Picks the tree for the current entry mode.
 *
 * A platform host resolves no tenant, so none of the storefront gates can be satisfied
 * there — PublicTenantGate alone would refuse the page for want of a RestaurantId. The
 * branch sits inside PublicTenantProvider so both trees read the same entry-mode
 * decision; nothing here parses the hostname a second time.
 *
 * The storefront branch is the provider chain exactly as it was before platform mode
 * existed, including for rasoix.app/r/{slug}, which the entry-mode resolver still
 * classifies as an alias storefront.
 */
function AppEntry() {
  const { entryMode } = usePublicTenant()

  if (entryMode === 'platform') {
    return <PlatformRoutes />
  }

  return (
    <PublicTenantGate>
      <CartTenantHydrationGate>
        <RestaurantConfigurationProvider>
          <RestaurantBootstrapGate>
            <AuthBootstrapGate>
              <WebRoutes />
            </AuthBootstrapGate>
          </RestaurantBootstrapGate>
        </RestaurantConfigurationProvider>
      </CartTenantHydrationGate>
    </PublicTenantGate>
  )
}

function App() {

  if (window.location.pathname.startsWith('/payment-gateway-response')) {
    return <PaymentGatewayResponse />
  }

  return (

    <Provider store={store}>
      <PersistGate
        loading={<SplashScreen />}
        persistor={persistor}
        onBeforeLift={() => {
          logMenuDiag('redux_persist_hydration_complete')
        }}
      >
        <BrowserRouter>
          <AppThemeProvider>
            <PublicTenantProvider>
              <AppEntry />
            </PublicTenantProvider>
          </AppThemeProvider>
        </BrowserRouter>
      </PersistGate>
    </Provider>
  )
}

export default App
